import { useState, useRef, useCallback } from 'react';
import L from 'leaflet';

/**
 * Manages the calibration workflow for the Plan Takeoff map.
 *
 * Calibration establishes the pixelsPerFoot scale factor by having the user
 * draw a line of known real-world length over the plan image.
 *
 * Workflow:
 *  1. Call startCalibration() — activates a 2-point polyline draw on the map.
 *  2. User draws a line; pendingLine is set with the pixel coords of that line.
 *  3. A CalibrationDialog prompts the user for the real-world distance.
 *  4. Call confirmCalibration(realDistance, unit) — computes pixelsPerFoot,
 *     renders a dashed red reference line with endpoint markers, and saves state.
 *  5. Call clearCalibration() to remove the reference line and reset state.
 *
 * In CRS.Simple, Leaflet stores [lat=pixelY, lng=pixelX], so we convert
 * to { x, y } pixel objects for consistency with pixelMeasurements.js.
 */
export default function useCalibration({ mapRef, drawnItemsRef }) {
  const [calibration, setCalibration] = useState(null);
  // calibration shape: { pixelDistance: number, realDistance: number, unit: string }

  const [calibrating, setCalibrating] = useState(false);
  const [pendingLine, setPendingLine] = useState(null);
  // pendingLine shape: [{ x, y }, { x, y }] — the two endpoints in pixel space

  const lineLayerRef = useRef(null);    // Reference line polyline layer
  const markerLayersRef = useRef([]);   // Endpoint marker layers

  // -----------------------------------------------------------------------
  // Internal helpers
  // -----------------------------------------------------------------------

  /** Convert Leaflet LatLng (CRS.Simple) to pixel { x, y }. */
  const toPixel = (ll) => ({ x: ll.lng, y: ll.lat });

  /** Convert pixel { x, y } to Leaflet LatLng for CRS.Simple. */
  const toLatLng = (pt) => L.latLng(pt.y, pt.x);

  /** Euclidean pixel distance between two pixel points. */
  const pixelDist = (a, b) => {
    const dx = b.x - a.x;
    const dy = b.y - a.y;
    return Math.sqrt(dx * dx + dy * dy);
  };

  /** Remove previously rendered calibration reference layers from the map. */
  const removeReferenceLayers = useCallback(() => {
    const map = mapRef.current;
    if (!map) return;

    if (lineLayerRef.current) {
      try { map.removeLayer(lineLayerRef.current); } catch (_) {}
      lineLayerRef.current = null;
    }
    markerLayersRef.current.forEach((m) => {
      try { map.removeLayer(m); } catch (_) {}
    });
    markerLayersRef.current = [];
  }, [mapRef]);

  /** Render the reference line and its endpoint markers on the map. */
  const renderReferenceLine = useCallback((pixelPoints) => {
    const map = mapRef.current;
    if (!map || pixelPoints.length < 2) return;

    removeReferenceLayers();

    const latlngs = pixelPoints.map(toLatLng);

    // Dashed red reference line
    const refLine = L.polyline(latlngs, {
      color: '#e53935',
      weight: 2,
      dashArray: '8, 6',
      opacity: 0.9,
    }).addTo(map);
    lineLayerRef.current = refLine;

    // Circular endpoint markers
    const circleIcon = L.divIcon({
      className: '',
      html: '<div style="width:10px;height:10px;border-radius:50%;background:#e53935;border:2px solid #fff;box-shadow:0 0 3px rgba(0,0,0,0.5);"></div>',
      iconSize: [10, 10],
      iconAnchor: [5, 5],
    });

    pixelPoints.forEach((pt) => {
      const marker = L.marker(toLatLng(pt), { icon: circleIcon, interactive: false }).addTo(map);
      markerLayersRef.current.push(marker);
    });
  }, [mapRef, removeReferenceLayers]);

  // -----------------------------------------------------------------------
  // Public API
  // -----------------------------------------------------------------------

  /**
   * Activate a 2-point polyline draw so the user can trace a known dimension
   * on the plan image. Sets pendingLine when the line is completed.
   */
  const startCalibration = useCallback(() => {
    const map = mapRef.current;
    if (!map) return;

    setCalibrating(true);
    setPendingLine(null);

    const handler = new L.Draw.Polyline(map, {
      shapeOptions: {
        color: '#e53935',
        weight: 2,
        dashArray: '8, 6',
        opacity: 0.9,
      },
      maxPoints: 2, // Only 2 points for a calibration segment
    });

    const listener = (e) => {
      map.off(L.Draw.Event.CREATED, listener);

      const layer = e.layer;
      const latlngs = layer.getLatLngs();

      // Remove the temporary draw layer immediately
      try {
        if (drawnItemsRef.current) drawnItemsRef.current.removeLayer(layer);
      } catch (_) {}
      // Also try removing directly from map in case it was added there
      try { map.removeLayer(layer); } catch (_) {}

      if (latlngs.length < 2) {
        setCalibrating(false);
        return;
      }

      const pixels = latlngs.map(toPixel);
      setPendingLine(pixels);
      // setCalibrating stays true until confirmCalibration or clearCalibration
    };

    map.on(L.Draw.Event.CREATED, listener);
    handler.enable();
  }, [mapRef, drawnItemsRef]);

  /**
   * Confirm the calibration by providing the real-world distance of pendingLine.
   * Computes pixelsPerFoot, stores calibration state, and renders the reference line.
   *
   * @param {number} realDistance - The real-world length of the drawn line.
   * @param {'ft'|'in'|'meters'} unit - Unit of the provided distance.
   */
  const confirmCalibration = useCallback((realDistance, unit) => {
    if (!pendingLine || pendingLine.length < 2) return;

    // Convert realDistance to feet
    let realFeet;
    switch (unit) {
      case 'in':
        realFeet = realDistance / 12;
        break;
      case 'meters':
        realFeet = realDistance * 3.28084;
        break;
      case 'ft':
      default:
        realFeet = realDistance;
        break;
    }

    if (!realFeet || realFeet <= 0) return;

    const pixelDistance = pixelDist(pendingLine[0], pendingLine[1]);
    const newCalibration = {
      pixelDistance,
      realDistance,
      unit,
      realFeet,
    };

    setCalibration(newCalibration);
    setCalibrating(false);
    renderReferenceLine(pendingLine);
    setPendingLine(null);
  }, [pendingLine, renderReferenceLine]);

  /**
   * Remove the calibration reference line and reset all calibration state.
   */
  const clearCalibration = useCallback(() => {
    removeReferenceLayers();
    setCalibration(null);
    setCalibrating(false);
    setPendingLine(null);
  }, [removeReferenceLayers]);

  /**
   * Derived scale factor in pixels per foot.
   * Returns 0 when no calibration has been confirmed.
   */
  const pixelsPerFoot = calibration
    ? calibration.pixelDistance / calibration.realFeet
    : 0;

  return {
    calibration,
    setCalibration,
    calibrating,
    pendingLine,
    startCalibration,
    confirmCalibration,
    clearCalibration,
    pixelsPerFoot,
  };
}
